import { NSARouterMakerResult } from "namirasoft-account-react";
import { FilterItem, FilterItemOperator } from "namirasoft-core";
import { ChatRow, ChatType, NamirasoftExpertMetaDatabase } from "namirasoft-expert";
import { NSButton, NSLine, NSLoading } from "namirasoft-site-react";
import { Component, createRef, UIEvent } from "react";
import { ExpertRouterProps } from "../ExpertRouterProps";
import { ExpertRoutes } from "../ExpertRoutes";
import Styles from "./ChatSidebar.module.css";

const PAGE_SIZE = 100;

const DEFAULT_TYPE_ICON = { src: "https://static.namirasoft.com/image/namirasoft/expert/logo/base.png", alt: "Chat" };

const TYPE_ICONS: Partial<Record<ChatType, { src: string; alt: string }>> = {
    // todo create proper icon for each
    [ChatType.Agent]: { src: "https://static.namirasoft.com/image/namirasoft/expert/logo/base.png", alt: "Agent" },
    [ChatType.Test]: { src: "https://static.namirasoft.com/image/concept/check/on/green.svg", alt: "Agent" },
};

function getDateGroupLabel(date: Date): string
{
    let now = new Date();
    let today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    let target = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    let diffDays = Math.floor((today.getTime() - target.getTime()) / (1000 * 60 * 60 * 24));

    if (diffDays <= 0) return "Today";
    if (diffDays === 1) return "Yesterday";
    if (target.getFullYear() === today.getFullYear())
        return target.toLocaleString("en-US", { month: "long", day: "numeric" });
    return target.toLocaleString("en-US", { month: "long", day: "numeric", year: "numeric" });
}

function groupChatsByDate(chats: ChatRow[]): { label: string; items: ChatRow[] }[]
{
    let groups: { label: string; items: ChatRow[] }[] = [];
    let lookup = new Map<string, ChatRow[]>();
    for (let chat of chats)
    {
        let label = getDateGroupLabel(new Date(chat.updated_at));
        let bucket = lookup.get(label);
        if (!bucket)
        {
            bucket = [];
            lookup.set(label, bucket);
            groups.push({ label, items: bucket });
        }
        bucket.push(chat);
    }
    return groups;
}

interface ChatSidebarProps extends NSARouterMakerResult, ExpertRouterProps
{
    agent_id: string;
    onItemClick: (path: string) => void;
}

interface ChatSidebarState
{
    open: boolean;
    selected_chat?: ChatRow;
    chats: ChatRow[];
    page: number;
    total: number;
    loading: boolean;
    initialized: boolean;
}

class ChatSidebar extends Component<ChatSidebarProps, ChatSidebarState>
{
    private HistoryListRef = createRef<HTMLUListElement>();

    constructor(props: ChatSidebarProps)
    {
        super(props);
        this.state = {
            open: false,
            chats: [],
            page: 0,
            total: 0,
            loading: false,
            initialized: false,
        };
        this.outsideClick = this.outsideClick.bind(this);
        this.onScroll = this.onScroll.bind(this);
    }

    override componentDidMount(): void
    {
        window.addEventListener("click", this.outsideClick);
    }

    override componentWillUnmount(): void
    {
        window.removeEventListener("click", this.outsideClick);
    }

    public setOpen(open: boolean)
    {
        this.setState(() => ({ open }), () =>
        {
            if (open && !this.state.initialized)
                this.loadChats();
        });
    }

    private outsideClick(event: MouseEvent)
    {
        let target = event.target as Node;
        if (!this.HistoryListRef.current?.contains(target))
            this.setState(() => ({ selected_chat: undefined }));
    }

    private async loadChats()
    {
        if (this.state.loading)
            return;
        let next_page = this.state.page + 1;
        if (this.state.initialized && this.state.chats.length >= this.state.total)
            return;

        this.setState((prev) => ({ ...prev, loading: true }));
        try
        {
            let expertMeta = new NamirasoftExpertMetaDatabase();
            let filters = [
                new FilterItem(
                    expertMeta.tables.chat,
                    expertMeta.tables.chat.columns.agent_id,
                    false,
                    FilterItemOperator.all.equals,
                    this.props.agent_id,
                ),
            ];
            let response = await this.props.servers.expert.chat.List(filters, next_page, PAGE_SIZE, [], null);
            this.setState((prev) => ({
                ...prev,
                chats: [...prev.chats, ...response.rows],
                page: next_page,
                total: response.count,
                loading: false,
                initialized: true,
            }));
        }
        catch
        {
            this.setState((prev) => ({ ...prev, loading: false, initialized: true }));
        }
    }

    private onScroll(event: UIEvent<HTMLUListElement>)
    {
        let el = event.currentTarget;
        if (this.state.loading) return;
        if (this.state.chats.length >= this.state.total) return;
        if (el.scrollTop + el.clientHeight >= el.scrollHeight - 64)
            this.loadChats();
    }

    override render()
    {
        return (
            <div className={`${Styles.ns_sidebar} ${this.state.open ? Styles.open : ""}`}>
                <div className={Styles.ns_sidebar_content}>
                    <div className={Styles.ns_sidebar_header}>
                        <NSButton
                            title={this.state.open ? "New Chat" : ""}
                            onClick={{
                                showLoading: false,
                                action: () =>
                                {
                                    this.props.url.redirect(ExpertRoutes.Chat_AgentNew(this.props.agent_id), {});
                                }
                            }}
                            classList={[Styles.ns_sidebar_btn]}
                            icon={{ src: "https://static.namirasoft.com/image/concept/new-chat/blue.svg", alt: "New Chat" }}
                        />

                        <NSLine classList={["w-100 bg-secondary"]} />

                        <NSButton
                            title={this.state.open ? "Chat History" : ""}
                            onClick={{
                                showLoading: false,
                                action: () => this.setOpen(!this.state.open)
                            }}
                            classList={[Styles.ns_sidebar_btn]}
                            icon={{ src: "https://static.namirasoft.com/image/concept/history/blue.svg", alt: "Chat History" }}
                        />
                    </div>

                    {
                        this.state.open &&
                        <ul ref={this.HistoryListRef} className={Styles.ns_history_list} onScroll={this.onScroll}>
                            {
                                groupChatsByDate(this.state.chats).map((group) => (
                                    <li key={group.label} className={Styles.ns_history_group}>
                                        <div className={Styles.ns_history_group_title}>{group.label}</div>
                                        <ul className={Styles.ns_history_group_list}>
                                            {
                                                group.items.map((item) =>
                                                {
                                                    let icon = TYPE_ICONS[item.type] ?? DEFAULT_TYPE_ICON;
                                                    return (
                                                        <li key={item.id} className={`${Styles.ns_history_item} ${this.state.selected_chat?.id === item.id ? Styles.selected : ""}`} onClick={() => this.props.onItemClick(ExpertRoutes.ChatPage(item))}>
                                                            <img
                                                                className={Styles.ns_history_item_type_icon}
                                                                src={icon.src}
                                                                alt={icon.alt}
                                                                title={icon.alt}
                                                            />
                                                            <div className={Styles.ns_history_item_content}>{item.name}</div>
                                                            <NSButton
                                                                title=""
                                                                classList={[Styles.ns_sidebar_btn, Styles.static]}
                                                                icon={{ src: "https://static.namirasoft.com/image/concept/ellipsis/vertical-blue.svg", alt: "Options" }}
                                                                onClick={{
                                                                    showLoading: false,
                                                                    action: () => this.setState((prev) => ({ selected_chat: prev.selected_chat?.id !== item.id ? item : undefined }))
                                                                }}
                                                            />
                                                            <div className={Styles.ns_history_item_actions} style={{ display: this.state.selected_chat?.id === item.id ? "flex" : "none" }}>
                                                                <NSButton
                                                                    title="Rename"
                                                                    onClick={{
                                                                        showLoading: false,
                                                                        action: () => { }
                                                                    }}
                                                                    classList={[Styles.ns_sidebar_btn]}
                                                                    icon={{ src: "https://static.namirasoft.com/image/concept/edit/blue.svg", alt: "Rename" }}
                                                                />
                                                                <NSButton
                                                                    title="Delete"
                                                                    onClick={{
                                                                        showLoading: false,
                                                                        action: () => { }
                                                                    }}
                                                                    classList={[Styles.ns_sidebar_btn]}
                                                                    icon={{ src: "https://static.namirasoft.com/image/concept/delete/blue.svg", alt: "Delete" }}
                                                                />
                                                            </div>
                                                        </li>
                                                    );
                                                })
                                            }
                                        </ul>
                                    </li>
                                ))
                            }
                            {
                                this.state.loading &&
                                <li className={Styles.ns_history_item}>
                                    <NSLoading />
                                </li>
                            }
                        </ul>
                    }
                </div>
            </div>
        );
    }
}

export default ChatSidebar;